import { createAppKit } from "@reown/appkit/react";
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import { SolanaAdapter } from "@reown/appkit-adapter-solana";
import { BitcoinAdapter } from "@reown/appkit-adapter-bitcoin";
import { TronAdapter } from "@reown/appkit-adapter-tron";
import { TronLinkAdapter } from "@tronweb3/tronwallet-adapter-tronlink";
import {
  arbitrum,
  base,
  bitcoin,
  bitcoinTestnet,
  mainnet,
  optimism,
  polygon,
  sepolia,
  solana,
  solanaDevnet,
  tronMainnet,
  tronShastaTestnet,
} from "@reown/appkit/networks";
import type { AppKitNetwork } from "@reown/appkit/networks";
import { ApiController, ChainController } from "@reown/appkit-controllers";
import { WalletConnectNamespace, WalletMetadata } from "app/shared/enums";

/**
 * Reown AppKit (formerly WalletConnect) configuration.
 *
 * This replaces the bespoke EIP-6963 / Wallet-Standard extension detection and the
 * raw `@walletconnect/universal-provider` QR flow for every chain AppKit supports
 * natively (EVM, Solana, Bitcoin, and TRON). AppKit renders a single native modal that
 * both lists detected browser extensions and shows a WalletConnect QR code — the DeFx
 * pattern. XRP and other chains without a dedicated AppKit adapter (Litecoin, Bitcoin
 * Cash, Dogecoin) keep the legacy manual-address-entry flow.
 */

const projectId = process.env.REACT_APP_REOWN_PROJECT_ID || "6fdb25c7faa5674d5af25462c1a8232b";

const enableTestnets = process.env.REACT_APP_ENABLE_TESTNETS === "true";

// EVM networks handled by the wagmi adapter.
const evmNetworks: Array<AppKitNetwork> = [
  mainnet,
  polygon,
  optimism,
  arbitrum,
  base,
  ...(enableTestnets ? [sepolia] : []),
];

// Solana + Bitcoin + Tron networks handled by their dedicated adapters.
const solanaNetworks: Array<AppKitNetwork> = [solana, ...(enableTestnets ? [solanaDevnet] : [])];
const bitcoinNetworks: Array<AppKitNetwork> = [bitcoin, ...(enableTestnets ? [bitcoinTestnet] : [])];
const tronNetworks: Array<AppKitNetwork> = [tronMainnet, ...(enableTestnets ? [tronShastaTestnet] : [])];

// createAppKit requires a non-empty tuple containing every network across all adapters.
const allNetworks = [...evmNetworks, ...solanaNetworks, ...bitcoinNetworks, ...tronNetworks] as [
  AppKitNetwork,
  ...Array<AppKitNetwork>,
];

const wagmiAdapter = new WagmiAdapter({
  networks: evmNetworks,
  projectId,
  ssr: false,
});

const solanaAdapter = new SolanaAdapter();
const bitcoinAdapter = new BitcoinAdapter();
// Tron has no universal wallet-detection standard (no EIP-6963 equivalent), so AppKit
// requires each supported wallet's adapter listed explicitly. We register ONLY TronLink.
// The MetaMask-Tron bridge (@tronweb3/tronwallet-adapter-metamask-tron) is intentionally
// NOT registered: it exposes the same MetaMask extension as a Tron wallet, which made
// AppKit route MetaMask through the Tron adapter and hand back a Tron address when a user
// picked MetaMask to pay for an EVM currency — leaving the eip155 account we need empty.
// To add more Tron wallets (Trust, OKX, BitKeep) list their adapters here the same way.
const tronAdapter = new TronAdapter({
  walletAdapters: [new TronLinkAdapter({ openUrlWhenWalletNotFound: false, checkTimeout: 3000 })],
});

/** wagmi config derived from the AppKit adapter — used by <WagmiProvider>. */
export const wagmiConfig = wagmiAdapter.wagmiConfig;

/** The global AppKit modal instance. Also opened via the `useAppKit()` hook. */
export const appKitModal = createAppKit({
  adapters: [wagmiAdapter, solanaAdapter, bitcoinAdapter, tronAdapter],
  networks: allNetworks,
  projectId,
  metadata: {
    name: WalletMetadata.NAME,
    description: WalletMetadata.DESCRIPTION,
    url: typeof window !== "undefined" ? window.location.origin : "",
    icons: [typeof window !== "undefined" ? `${window.location.origin}/relm-icon.svg` : ""],
  },
  // Wallet-only experience (no email / social login)
  // `reownBranding: false` hides the "UX by reown" badge AppKit shows by default. It's a
  // real, working option that AppKit's own runtime config reader (ConfigUtil.js) reads
  // off this object — this SDK version's `Features` TS type just hasn't caught up to
  // declare it yet, hence the `as any`. It only takes effect when AppKit isn't
  // overriding it via the project's remote config — see the note in the response about
  // dashboard.reown.com if the badge still shows up.
  features: {
    email: false,
    socials: false,
    analytics: false,
    reownBranding: false,
  } as any,
  // Show ONLY the user's installed browser extensions (+ WalletConnect QR) — not AppKit's
  // catalog. `allWallets: "HIDE"` removes the "Search Wallet" browse-all button, and no
  // featured wallets are pinned. (Recommended wallets already auto-hide once 4+ extensions
  // are detected — see getCappedRecommendedWallets in AppKit.)

  // allWallets: "HIDE",
  featuredWalletIds: [],
  // Use AppKit's own floating overlay modal (wallet list + WalletConnect QR), popped up
  // over the page the same way the rest of checkout's modals work — rather than
  // embedding the connect UI inline in the panel. Inline embedding required fragile
  // shadow-DOM hacks to stop AppKit's own view-height changes (list -> WalletConnect QR
  // -> mobile deep link) from visibly shifting the surrounding page layout every time
  // AppKit swapped views. As a popup those swaps just resize the floating dialog.
  enableEmbedded: false,
  // Do NOT auto-reconnect the last-used wallet on page load — otherwise MetaMask (or
  // whichever wallet) prompts before the user has even chosen a currency. The wallet is
  // only touched once the user selects a currency and connects via the inline UI.
  enableReconnect: false,
  themeMode: "light",
  themeVariables: {
    "--w3m-accent": "#9C33E5",
    "--w3m-color-mix": "#3d2a66",
    "--w3m-color-mix-strength": 8,
    "--w3m-border-radius-master": "2px",
  },
});

// Warm up AppKit's wallet discovery at startup. Without this the FIRST time the connect
// modal opens it shows the "recommended" registry list (each wallet with all its chains),
// then swaps to the detected/installed list once EIP-6963 detection settles — which looks
// inconsistent. Prefetching here means the first open already shows the settled list.
if (typeof window !== "undefined") {
  void Promise.resolve().then(async () => {
    try {
      await ApiController.prefetch();
      await ApiController.fetchWalletsByPage({ page: 1 });
    } catch {
      // Non-fatal — the list still loads lazily when the modal opens.
    }
  });
}

export type AppKitSupportedNamespace =
  | WalletConnectNamespace.EVM
  | WalletConnectNamespace.SOLANA
  | WalletConnectNamespace.BITCOIN
  | WalletConnectNamespace.TRON;

const EVM_NATIVE_CURRENCIES = new Set([
  "ETH",
  "MATIC",
  "POL",
  "BNB",
  "AVAX",
  "OP",
  "ARB",
  "BASE",
  "XDAI",
  "FTM",
]);

/**
 * Maps a network's native currency to the AppKit chain namespace, or `null` when the
 * chain is not natively supported by AppKit (XRP, Litecoin, Bitcoin Cash, Dogecoin, and
 * any other chain without a dedicated adapter) and must use the legacy
 * manual-address-entry fallback.
 */
export function getAppKitNamespace(nativeCurrency?: string | null): AppKitSupportedNamespace | null {
  if (!nativeCurrency) return null;

  const code = nativeCurrency.trim().toUpperCase();

  if (code === "SOL") return WalletConnectNamespace.SOLANA;
  if (code === "BTC") return WalletConnectNamespace.BITCOIN;
  if (code === "TRX") return WalletConnectNamespace.TRON;
  if (EVM_NATIVE_CURRENCIES.has(code)) return WalletConnectNamespace.EVM;

  return null;
}

/**
 * Returns the default (mainnet) network for a namespace. Before connecting we switch
 * AppKit's ACTIVE chain to this, because AppKit only fully syncs the account (caipAddress)
 * for the active chain over WalletConnect — without it, connecting for e.g. an EVM
 * currency while another namespace is active leaves the EVM account empty and we can't
 * read the address.
 */
export function getNamespaceNetwork(namespace: AppKitSupportedNamespace): AppKitNetwork {
  switch (namespace) {
    case WalletConnectNamespace.SOLANA:
      return solanaNetworks[0];
    case WalletConnectNamespace.BITCOIN:
      return bitcoinNetworks[0];
    case WalletConnectNamespace.TRON:
      return tronNetworks[0];
    case WalletConnectNamespace.EVM:
    default:
      return evmNetworks[0];
  }
}

/**
 * Restrict the namespaces a WalletConnect connection REQUESTS to just `namespace`.
 *
 * AppKit builds a connection's `optionalNamespaces` from
 * `ChainController.getAllRequestedCaipNetworks()` — every configured network across ALL
 * adapters — so even an EVM connect advertises `tron`. A wallet with a Tron snap
 * (MetaMask) then satisfies the request with its Tron account and returns NO eip155
 * account, leaving the EVM address empty. By emptying every OTHER namespace's requested
 * networks before connecting, the WC request advertises only the target namespace, so the
 * wallet must return that chain's account (or nothing). Always pair with
 * `restoreWalletConnectNamespaces()`.
 */
export function scopeWalletConnectToNamespace(namespace: AppKitSupportedNamespace): void {
  ChainController.state.chains.forEach((adapter, chain) => {
    const networks = adapter.caipNetworks ?? [];
    ChainController.setRequestedCaipNetworks(chain === namespace ? networks : [], chain);
  });
}

/** Restore every adapter's requested networks (undoes scopeWalletConnectToNamespace). */
export function restoreWalletConnectNamespaces(): void {
  ChainController.state.chains.forEach((adapter, chain) => {
    ChainController.setRequestedCaipNetworks(adapter.caipNetworks ?? [], chain);
  });
}

const DEFAULT_WALLET_ACCENT = "#9C33E5";

/** Re-themes the wallet-connect modal with the partner's primary colour (whitelabel). */
export function applyBrandingToWalletModal(brandColor?: string | null): void {
  try {
    appKitModal.setThemeVariables({ "--w3m-accent": brandColor || DEFAULT_WALLET_ACCENT });
  } catch (err) {
    console.error("Failed to apply branding to wallet modal:", err);
  }
}
